/**
 * Knowledge Base Service for Client-Side Operations
 * Handles all knowledge base document CRUD operations using Supabase directly
 * Matches the exact structure from server/services/supabase-knowledgebase.service.ts
 */

import { supabase, isSupabaseConfigured, getCurrentUserId } from "@/lib/supabase";
import { ragService } from "@/services/ragService";
import { getThoughtProcessPreferences } from "@/lib/thoughtProcessPreferences";

function scheduleOperatingBrief(userId: string, documentId: string, title: string, content: unknown): void {
    const text = typeof content === "string" ? content : "";
    if (!userId || !documentId || !text.trim()) return;
    void import("@/services/operatingLoopService").then(({ scheduleOperatingBrief: schedule }) =>
        schedule({ userId, documentId, title: title || "Note", content: text }),
    );
}

/**
 * KnowledgeBase item interface - matches server interface
 */
export interface KnowledgeBaseItem {
    id: string;
    userId: string;
    title: string;
    spaceId?: string | null;
    spaceIds?: string[]; // Array of space IDs (for documents with multiple spaces)
    topicId?: string | null;
    topicIds?: string[]; // Array of topic IDs (for documents with multiple topics)
    tagIds?: string[];
    type?: string | null;
    content?: string | null;
    folderId?: string | null;
    googleDocsId?: string | null;
    videoUrl?: string | null;
    metadata?: any;
    /** Denormalized from metadata.mergedFromSpaceId (set by merge RPC; cleared on restore). Used for “Merged” UI in space detail. */
    mergedFromSpaceId?: string | null;
    isLocked?: boolean;
    lockedAt?: string | null;
    isSource?: boolean;
    /** When false, document is excluded from RAG retrieval even if embedded. Default true. */
    includeInRag?: boolean;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * Insert KnowledgeBase item interface
 */
export interface InsertKnowledgeBaseItem {
    title: string;
    spaceId?: string | null;
    spaceIds?: string[]; // Array of space IDs (for documents with multiple spaces)
    topicId?: string | null;
    topicIds?: string[]; // Array of topic IDs (for documents with multiple topics)
    tagIds?: string[];
    type?: string | null;
    content?: string | null;
    folderId?: string | null;
    googleDocsId?: string | null;
    videoUrl?: string | null;
    metadata?: any;
    includeInRag?: boolean;
}

/**
 * Transform Supabase data to frontend format
 * Use metadata timestamps as primary source for accurate time display
 * Matches server implementation exactly
 */
function transformToFrontend(data: any): KnowledgeBaseItem {
    let metadata = data.metadata || {};
    
    if (typeof metadata === 'string') {
        try {
            metadata = JSON.parse(metadata);
        } catch (e) {
            metadata = {};
        }
    }
    
    const createdAt = metadata.timestampCreatedAt || data.createdAt || data.created_at;
    const updatedAt = metadata.timestampUpdatedAt || data.updatedAt || data.updated_at;
    
    // Support both spaceIds array (new) and spaceId single value (backward compatibility)
    const spaceIds = Array.isArray(metadata.spaceIds) ? metadata.spaceIds : (Array.isArray(data.spaceIds) ? data.spaceIds : []);
    const spaceId = spaceIds.length > 0 ? spaceIds[0] : (metadata.spaceId || data.spaceId || data.space_id || null);
    
    // Support both topicIds array (new) and topicId single value (backward compatibility)
    const topicIds = Array.isArray(metadata.topicIds) ? metadata.topicIds : (Array.isArray(data.topicIds) ? data.topicIds : []);
    const topicId = topicIds.length > 0 ? topicIds[0] : (metadata.topicId || data.topicId || data.topic_id || null);
    
    const tagIds = metadata.tagIds || data.tagIds || data.tag_ids || [];
    
    const isSource = metadata.isSource === true || data.isSource === true || data.is_source === true;
    const metaLocked = metadata.isLocked === true || metadata.isLocked === "true";
    const metaLockedAt = !!(metadata as { lockedAt?: string }).lockedAt;
    const dataLocked = data.isLocked === true || data.is_locked === true || !!(data.lockedAt || data.locked_at);
    const isLocked = metaLocked || metaLockedAt || dataLocked;
    
    const includeInRag = (data.includeInRag ?? data.include_in_rag) !== false;
    const mergedFromMeta = (metadata as { mergedFromSpaceId?: string }).mergedFromSpaceId;
    const mergedFromSpaceId =
        mergedFromMeta != null && mergedFromMeta !== "" ? String(mergedFromMeta) : undefined;
    return {
        id: data.id,
        userId: data.userId || data.user_id,
        title: data.title,
        spaceId, // Keep for backward compatibility
        spaceIds, // Include new field
        topicId, // Keep for backward compatibility
        topicIds, // Include new field
        tagIds,
        type: data.type,
        content: data.content,
        folderId: data.folderId || data.folder_id,
        googleDocsId: data.googleDocsId || data.google_docs_id,
        videoUrl: data.videoUrl || data.video_url,
        metadata: data.metadata,
        mergedFromSpaceId,
        isLocked,
        lockedAt: data.lockedAt || data.locked_at,
        isSource,
        includeInRag,
        createdAt,
        updatedAt
    };
}

/** Column or metadata.spaceId / metadata.spaceIds — for client-side space filtering. */
export function isKnowledgeBaseDocLinkedToSpace(doc: KnowledgeBaseItem, spaceId: string): boolean {
    if (!spaceId) return false;
    const sid = String(spaceId);
    if (doc.spaceId != null && String(doc.spaceId) === sid) return true;
    if (Array.isArray(doc.spaceIds) && doc.spaceIds.some((id) => String(id) === sid)) return true;
    let meta = doc.metadata;
    if (typeof meta === "string") {
        try {
            meta = JSON.parse(meta);
        } catch {
            meta = null;
        }
    }
    if (meta && typeof meta === "object") {
        const m = meta as Record<string, unknown>;
        if (m.spaceId != null && String(m.spaceId) === sid) return true;
        if (Array.isArray(m.spaceIds) && m.spaceIds.some((id) => String(id) === sid)) return true;
    }
    return false;
}

/** True when the KB row is associated with the given topic (column, topicIds, or metadata). Matches TopicsManagement filtering. */
export function isKnowledgeBaseDocLinkedToTopic(doc: KnowledgeBaseItem, topicId: string): boolean {
    const tid = String(topicId);
    if (doc.topicId != null && String(doc.topicId) === tid) return true;
    if (Array.isArray(doc.topicIds) && doc.topicIds.some((id) => String(id) === tid)) return true;
    let meta = doc.metadata;
    if (typeof meta === "string") {
        try {
            meta = JSON.parse(meta);
        } catch {
            meta = null;
        }
    }
    if (meta && typeof meta === "object") {
        const m = meta as Record<string, unknown>;
        if (m.topicId != null && String(m.topicId) === tid) return true;
        if (Array.isArray(m.topicIds) && m.topicIds.some((id) => String(id) === tid)) return true;
    }
    return false;
}

class KnowledgeBaseService {
    async getKnowledgeBaseSummaries(options?: {
        spaceId?: string | null;
        workspaceId?: string | null;
        /** When true, return this user's documents in every workspace. */
        allWorkspaces?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<KnowledgeBaseItem[]> {

        const { spaceId = null, workspaceId = null, allWorkspaces = false, limit = 200, offset = 0 } = options || {};

        if (!isSupabaseConfigured) {
            throw new Error("Service not configured. Please try again later.");
        }

        const userId = await this.getCurrentUserId();

        // Select columns matching actual schema: camelCase for most, snake_case for is_locked and locked_at
        const selectFields = "id, userId, title, spaceId, topicId, type, folderId, googleDocsId, videoUrl, metadata, isLocked, is_locked, locked_at, includeInRag, createdAt, updatedAt";

        if (spaceId) {
            const { spaceSharingService } = await import("./spaceSharingService");
            const hasAccess = await spaceSharingService.hasSpaceAccess(spaceId);
            if (!hasAccess) {
                return [];
            }

            const { data: space } = await supabase
                .from("mastermind")
                .select("user_id")
                .eq("id", spaceId)
                .single();

            if (!space) {
                return [];
            }

            const { data, error } = await supabase
                .from("knowledgebase")
                .select(selectFields)
                .eq("spaceId", spaceId)
                .eq("userId", space.user_id)
                .order("updatedAt", { ascending: false })
                .order("id", { ascending: true })
                .range(offset, offset + limit - 1);

            if (error) {
                console.error("Error fetching knowledgebase summaries:", error);
                throw new Error(`Failed to fetch knowledgebase summaries: ${error.message}`);
            }

            return (data || []).map(transformToFrontend);
        }

        let q = supabase
            .from("knowledgebase")
            .select(selectFields)
            .eq("userId", userId)
            .order("updatedAt", { ascending: false })
            .order("id", { ascending: true })
            .range(offset, offset + limit - 1);
        if (!allWorkspaces) {
            if (workspaceId != null && workspaceId !== "") {
                q = q.eq("workspace_id", workspaceId);
            } else {
                q = q.is("workspace_id", null);
            }
        }
        const { data, error } = await q;

        if (error) {
            console.error("Error fetching knowledgebase summaries:", error);
            throw new Error(`Failed to fetch knowledgebase summaries: ${error.message}`);
        }

        return (data || []).map(transformToFrontend);
    }

    /**
     * Document ids whose body is non-empty. Does not download content.
     * Whitespace-only bodies are not present in this library.
     */
    async listDocumentIdsWithContent(): Promise<string[]> {
        if (!isSupabaseConfigured) {
            throw new Error("Service not configured. Please try again later.");
        }
        const userId = await this.getCurrentUserId();
        const ids: string[] = [];
        const pageSize = 1000;
        let offset = 0;
        while (true) {
            const { data, error } = await supabase
                .from("knowledgebase")
                .select("id")
                .eq("userId", userId)
                .not("content", "is", null)
                .neq("content", "")
                .order("id", { ascending: true })
                .range(offset, offset + pageSize - 1);
            if (error) {
                throw new Error(`Failed to list documents with content: ${error.message}`);
            }
            const page = data || [];
            for (const row of page) {
                if (row?.id) ids.push(String(row.id));
            }
            if (page.length < pageSize) break;
            offset += pageSize;
        }
        return ids;
    }

    /**
     * Generate a simple unique ID - matches server implementation
     */
    private generateSimpleUniqueId(): string {
        const timestamp = Date.now().toString(36);
        const randomPart = Math.random().toString(36).substring(2, 9);
        return timestamp + randomPart;
    }

    /**
     * Get current user ID from Supabase session
     */
    private async getCurrentUserId(): Promise<string> {
        if (!isSupabaseConfigured) {
            throw new Error("Service not configured. Please try again later.");
        }

        const userId = await getCurrentUserId();
        if (!userId) {
            throw new Error("User not authenticated. Please log in.");
        }
        return userId;
    }

    /**
     * Get knowledgebase items filtered by spaceId and topicIds
     * Only fetches documents that are:
     * 1. Directly linked to the space (spaceId matches)
     * 2. OR linked to any of the provided topics (topicId in metadata matches any topicId)
     * Fetches documents owned by all space members (for shared spaces)
     * This is optimized for faster loading by only fetching relevant documents at the database level
     */
    async getKnowledgeBaseItemsForSpace(spaceId: string, topicIds: string[] = [], memberUserIds?: string[]): Promise<KnowledgeBaseItem[]> {
        // Check if user has access to this space (owner or shared)
        const { spaceSharingService } = await import("./spaceSharingService");
        const hasAccess = await spaceSharingService.hasSpaceAccess(spaceId);
        
        if (!hasAccess) {
            return [];
        }

        // Get space owner
        const { data: space } = await supabase
            .from("mastermind")
            .select("user_id")
            .eq("id", spaceId)
            .single();

        if (!space) {
            return [];
        }

        // Determine which user IDs to fetch documents for
        // If memberUserIds is provided (shared space), fetch for all members
        // Otherwise, just fetch for space owner (backward compatibility)
        const userIdsToFetch = memberUserIds && memberUserIds.length > 0 
            ? memberUserIds 
            : [space.user_id];

        const userIdSet = new Set(userIdsToFetch.map((id) => String(id)));
        const selectKb =
            "id, userId, title, spaceId, type, content, folderId, googleDocsId, videoUrl, metadata, createdAt, updatedAt, isLocked";

        // Space-linked rows: use RPC (column spaceId OR metadata.spaceIds / metadata.spaceId).
        // Plain .eq("spaceId", spaceId) misses rows with spaceId null but metadata linkage.
        const allDocs = new Map<string, any>();

        const { data: rpcData, error: rpcError } = await supabase.rpc("knowledgebase_by_space_link", {
            p_space_id: spaceId,
        });

        if (rpcError) {
            console.error("getKnowledgeBaseItemsForSpace (knowledgebase_by_space_link):", rpcError);
            throw new Error(`Failed to fetch knowledgebase for space: ${rpcError.message}`);
        }

        const rpcRows = Array.isArray(rpcData) ? rpcData : rpcData ? [rpcData] : [];
        for (const row of rpcRows) {
            const uid = row?.userId ?? row?.user_id;
            if (uid != null && userIdSet.has(String(uid)) && row?.id != null && !allDocs.has(row.id)) {
                allDocs.set(row.id, row);
            }
        }

        // Optional: topic-linked docs (when caller passes topicIds) — merge, dedupe by id
        if (topicIds.length > 0) {
            const topicConditions: string[] = [];
            topicIds.forEach((tid) => {
                topicConditions.push(`metadata->>'topicId'.eq.${tid}`);
            });
            topicIds.forEach((tid) => {
                topicConditions.push(`metadata->'topicIds'.cs.["${tid}"]`);
            });

            const { data: topicData, error: topicError } = await supabase
                .from("knowledgebase")
                .select(selectKb)
                .in("userId", userIdsToFetch)
                .or(topicConditions.join(","))
                .order("updatedAt", { ascending: false });

            if (topicError) {
                console.error("Error fetching knowledgebase items for space (topic query):", topicError);
            } else {
                for (const doc of topicData || []) {
                    if (doc?.id != null && !allDocs.has(doc.id)) {
                        allDocs.set(doc.id, doc);
                    }
                }
            }
        }

        const transformed = Array.from(allDocs.values()).map(transformToFrontend);

        return transformed.filter((doc) => {
            if (isKnowledgeBaseDocLinkedToSpace(doc, spaceId)) return true;

            if (topicIds.length > 0) {
                const docTopicIds = doc.topicIds || [];
                if (Array.isArray(docTopicIds) && docTopicIds.length > 0) {
                    const docTopicIdsStr = docTopicIds.map((id) => String(id));
                    if (topicIds.some((tid) => docTopicIdsStr.includes(String(tid)))) {
                        return true;
                    }
                }
                if (doc.topicId) {
                    const docTopicId = typeof doc.topicId === "string" ? doc.topicId : String(doc.topicId);
                    if (
                        topicIds.some((tid) => {
                            const normalizedTopicId = typeof tid === "string" ? tid : String(tid);
                            return (
                                docTopicId === normalizedTopicId ||
                                Number(docTopicId) === Number(normalizedTopicId)
                            );
                        })
                    ) {
                        return true;
                    }
                }
            }

            return false;
        });
    }

    /**
     * Documents linked to a specific topic within a space (knowledgebase rows), aligned with Topic UI:
     * metadata.topicIds / topicId (and column topicId) must reference the topic.
     */
    async getKnowledgeBaseItemsForTopic(
        spaceId: string,
        topicId: string,
        memberUserIds?: string[]
    ): Promise<KnowledgeBaseItem[]> {
        const items = await this.getKnowledgeBaseItemsForSpace(spaceId, [topicId], memberUserIds);
        return items.filter((doc) => isKnowledgeBaseDocLinkedToTopic(doc, topicId));
    }

    /**
     * Same filter as SQL (PostgREST .or on metadata is unreliable when metadata is text / cs encoding).
     * Uses DB function: knowledgebase_by_space_link(p_space_id text).
     */
    async getKnowledgeBaseItemsBySpaceColumnOrMetadata(spaceId: string): Promise<KnowledgeBaseItem[]> {
        const { spaceSharingService } = await import("./spaceSharingService");
        const hasAccess = await spaceSharingService.hasSpaceAccess(spaceId);
        if (!hasAccess) {
            return [];
        }

        const { data, error } = await supabase.rpc("knowledgebase_by_space_link", {
            p_space_id: spaceId,
        });

        if (error) {
            console.error("getKnowledgeBaseItemsBySpaceColumnOrMetadata (rpc):", error);
            throw new Error(`Failed to fetch knowledgebase by space link: ${error.message}`);
        }

        const rows = Array.isArray(data) ? data : data ? [data] : [];
        return rows.map(transformToFrontend);
    }

    /**
     * Get all knowledgebase items for the current user.
     * @param spaceId - Optional: filter by space.
     * @param workspaceId - When null/undefined: personal. When set: items for that workspace.
     */
    async getKnowledgeBaseItems(spaceId?: string | null, workspaceId?: string | null): Promise<KnowledgeBaseItem[]> {
        const userId = await this.getCurrentUserId();

        if (spaceId) {
            // Check if user has access to this space (owner or shared)
            const { spaceSharingService } = await import("./spaceSharingService");
            const hasAccess = await spaceSharingService.hasSpaceAccess(spaceId);
            
            if (!hasAccess) {
                // User doesn't have access, return empty array
                return [];
            }

            // Fetch documents from space owner (all documents in the space)
            // Get space owner
            const { data: space } = await supabase
                .from("mastermind")
                .select("user_id")
                .eq("id", spaceId)
                .single();

            if (!space) {
                return [];
            }

            // Fetch all documents for this space (owned by space owner)
            const { data, error } = await supabase
                .from("knowledgebase")
                .select("id, userId, title, spaceId, type, content, folderId, googleDocsId, videoUrl, metadata, createdAt, updatedAt, isLocked")
                .eq("spaceId", spaceId)
                .eq("userId", space.user_id) // Documents owned by space owner
                .order("updatedAt", { ascending: false });

            if (error) {
                console.error("Error fetching knowledgebase items:", error);
                throw new Error(`Failed to fetch knowledgebase items: ${error.message}`);
            }

            return (data || []).map(transformToFrontend);
        }

        // No spaceId - fetch user's documents for current context (personal or workspace)
        let q = supabase
            .from("knowledgebase")
            .select("id, userId, title, spaceId, type, content, folderId, googleDocsId, videoUrl, metadata, createdAt, updatedAt")
            .eq("userId", userId)
            .order("updatedAt", { ascending: false });
        if (workspaceId != null && workspaceId !== "") {
            q = q.eq("workspace_id", workspaceId);
        } else {
            q = q.is("workspace_id", null);
        }
        const { data, error } = await q;

        if (error) {
            console.error("Error fetching knowledgebase items:", error);
            throw new Error(`Failed to fetch knowledgebase items: ${error.message}`);
        }

        return (data || []).map(transformToFrontend);
    }

    /**
     * Get a single knowledgebase item by ID
     * Matches server implementation: getKnowledgeBaseItem
     */
    async getKnowledgeBaseItem(id: string): Promise<KnowledgeBaseItem | null> {
        const userId = await this.getCurrentUserId();

        const { data, error } = await supabase
            .from("knowledgebase")
            .select("id, userId, title, spaceId, type, content, folderId, googleDocsId, videoUrl, metadata, createdAt, updatedAt, isLocked")
            .eq("id", id)
            .eq("userId", userId)
            .maybeSingle();

        if (error && error.code !== "PGRST116") {
            console.error("Error fetching knowledgebase item:", error);
            throw new Error(`Failed to fetch knowledgebase item: ${error.message}`);
        }

        if (data) return transformToFrontend(data);

        const idStr = String(id ?? "").trim();
        if (!idStr) return null;

        // Single jsonb arg `payload` matches PostgREST reliably (see migration 021; avoids PGRST202 on p_id text).
        const { data: sharedRow, error: rpcError } = await supabase.rpc("shared_get_knowledgebase_item", {
            payload: { p_id: idStr },
        });
        if (rpcError) {
            console.warn("shared_get_knowledgebase_item:", rpcError.message);
            return null;
        }
        if (!sharedRow) return null;
        const raw = typeof sharedRow === "object" && sharedRow !== null && !Array.isArray(sharedRow)
            ? sharedRow
            : JSON.parse(String(sharedRow));
        return transformToFrontend(raw);
    }

    /**
     * Create a new knowledgebase item.
     * @param workspaceId - When null/undefined: personal. When set: document belongs to that workspace.
     */
    async createKnowledgeBaseItem(itemData: InsertKnowledgeBaseItem, workspaceId?: string | null): Promise<KnowledgeBaseItem> {
        const userId = await this.getCurrentUserId();

        if (!itemData.title || !itemData.title.trim()) {
            throw new Error("Title is required");
        }

        const now = new Date().toISOString();
        const itemToInsert: any = {
            id: this.generateSimpleUniqueId(),
            userId,
            title: itemData.title.trim(),
            spaceId: itemData.spaceId || null,
            type: itemData.type || null,
            content: itemData.content || null,
            folderId: itemData.folderId || null,
            googleDocsId: itemData.googleDocsId || null,
            videoUrl: itemData.videoUrl || null,
            includeInRag: itemData.includeInRag !== false,
            metadata: {
                ...itemData.metadata,
                // Support both spaceIds array (new) and spaceId single value (backward compatibility)
                // When spaceIds is provided, set spaceId to null to avoid dual values
                spaceIds: itemData.spaceIds !== undefined ? itemData.spaceIds : (itemData.spaceId ? [itemData.spaceId] : []),
                spaceId: itemData.spaceIds !== undefined ? null : (itemData.spaceId || null), // null when using spaceIds array
                // Support both topicIds array (new) and topicId single value (backward compatibility)
                // When topicIds is provided, set topicId to null to avoid dual values
                topicIds: itemData.topicIds !== undefined ? itemData.topicIds : (itemData.topicId ? [itemData.topicId] : []),
                topicId: itemData.topicIds !== undefined ? null : (itemData.topicId || null), // null when using topicIds array
                tagIds: itemData.tagIds || [],
                timestampCreatedAt: now,
                timestampUpdatedAt: now
            },
            createdAt: now,
            updatedAt: now
        };
        if (workspaceId != null && workspaceId !== "") {
            itemToInsert.workspace_id = workspaceId;
        } else {
            itemToInsert.workspace_id = null;
        }

        const { data, error } = await supabase
            .from("knowledgebase")
            .insert(itemToInsert)
            .select()
            .single();

        if (error) {
            console.error("Error creating knowledgebase item:", error);
            throw new Error(`Failed to create knowledgebase item: ${error.message}`);
        }

        if (!data) {
            throw new Error("Failed to create knowledgebase item: No data returned");
        }

        const item = transformToFrontend(data);
        if (itemData.content && String(itemData.content).trim().length > 0
            && getThoughtProcessPreferences().autoEmbedDocuments) {
            ragService.scheduleEmbedDocument(item.id);
        }
        scheduleOperatingBrief(item.userId, item.id, item.title, item.content);
        return item;
    }

    /**
     * Update an existing knowledgebase item
     * @param spaceContextId - When editing another user's document in a shared space, pass the space id (uses RPC).
     */
    async updateKnowledgeBaseItem(
        id: string,
        updates: Partial<InsertKnowledgeBaseItem>,
        spaceContextId?: string | null
    ): Promise<KnowledgeBaseItem | null> {
        const userId = await this.getCurrentUserId();

        const existing = await this.getKnowledgeBaseItem(id);
        if (!existing) {
            return null;
        }

        const isOwn = existing.userId === userId;
        if (!isOwn && !spaceContextId) {
            return null;
        }

        const now = new Date().toISOString();
        const updateData: any = {
            updatedAt: now,
        };

        let existingMetadata = existing.metadata || {};
        if (typeof existingMetadata === 'string') {
            try {
                existingMetadata = JSON.parse(existingMetadata);
            } catch (e) {
                existingMetadata = {};
            }
        }
        
        let newMetadata = updates.metadata || {};
        if (typeof newMetadata === 'string') {
            try {
                newMetadata = JSON.parse(newMetadata);
            } catch (e) {
                newMetadata = {};
            }
        }
        
        // Handle spaceIds array (new format) and spaceId single value (backward compatibility)
        let finalSpaceIds: string[] = [];
        let finalSpaceId: string | null = null;
        
        if (updates.spaceIds !== undefined) {
            // New format: spaceIds array - set spaceId to null to avoid dual values
            finalSpaceIds = updates.spaceIds;
            finalSpaceId = null; // Clear spaceId when using spaceIds array
        } else if (updates.spaceId !== undefined) {
            // Old format: single spaceId (convert to array for consistency)
            finalSpaceId = updates.spaceId;
            finalSpaceIds = updates.spaceId ? [updates.spaceId] : [];
        } else {
            // Keep existing values (for backward compatibility with old documents)
            if (Array.isArray(existingMetadata.spaceIds)) {
                // Document already uses new format - keep spaceId as null
                finalSpaceIds = existingMetadata.spaceIds;
                finalSpaceId = null;
            } else {
                // Document uses old format - keep both for backward compatibility
                finalSpaceId = existingMetadata.spaceId || (existing.spaceId || null);
                finalSpaceIds = finalSpaceId ? [finalSpaceId] : [];
            }
        }
        
        // Handle topicIds array (new format) and topicId single value (backward compatibility)
        let finalTopicIds: string[] = [];
        let finalTopicId: string | null = null;
        
        if (updates.topicIds !== undefined) {
            // New format: topicIds array - set topicId to null to avoid dual values
            finalTopicIds = updates.topicIds;
            finalTopicId = null; // Clear topicId when using topicIds array
        } else if (updates.topicId !== undefined) {
            // Old format: single topicId (convert to array for consistency)
            finalTopicId = updates.topicId;
            finalTopicIds = updates.topicId ? [updates.topicId] : [];
        } else {
            // Keep existing values (for backward compatibility with old documents)
            if (Array.isArray(existingMetadata.topicIds)) {
                // Document already uses new format - keep topicId as null
                finalTopicIds = existingMetadata.topicIds;
                finalTopicId = null;
            } else {
                // Document uses old format - keep both for backward compatibility
                finalTopicId = existingMetadata.topicId || null;
                finalTopicIds = finalTopicId ? [finalTopicId] : [];
            }
        }
        
        updateData.metadata = {
            ...existingMetadata,
            ...newMetadata,
            spaceIds: finalSpaceIds,
            spaceId: finalSpaceId, // null when using spaceIds array, or single value for old format
            topicIds: finalTopicIds,
            topicId: finalTopicId, // null when using topicIds array, or single value for old format
            tagIds: updates.tagIds !== undefined ? updates.tagIds : (existingMetadata.tagIds || []),
            timestampCreatedAt: existingMetadata.timestampCreatedAt,
            timestampUpdatedAt: now
        };
        
        // Also update spaceId column for backward compatibility (set to null when using spaceIds array)
        if (updates.spaceIds !== undefined) {
            updateData.spaceId = null; // Clear spaceId column when using spaceIds array
        } else if (updates.spaceId !== undefined) {
            updateData.spaceId = updates.spaceId; // Update spaceId column for old format
        }

        if (updates.title !== undefined) {
            if (!updates.title || !updates.title.trim()) {
                throw new Error("Title cannot be empty");
            }
            updateData.title = updates.title.trim();
        }
        if (updates.spaceId !== undefined) updateData.spaceId = updates.spaceId;
        if (updates.type !== undefined) updateData.type = updates.type;
        if (updates.content !== undefined) updateData.content = updates.content;
        if (updates.folderId !== undefined) updateData.folderId = updates.folderId;
        if (updates.googleDocsId !== undefined) updateData.googleDocsId = updates.googleDocsId;
        if (updates.videoUrl !== undefined) updateData.videoUrl = updates.videoUrl;
        // includeInRag is updated via RPC to avoid PGRST204 schema-cache issues
        const updateKeys = (Object.keys(updates) as (keyof typeof updates)[]).filter((k) => updates[k] !== undefined);
        const onlyIncludeInRag = updateKeys.length === 1 && updateKeys[0] === "includeInRag";

        if (updates.includeInRag !== undefined && isOwn) {
            const { data: rpcData, error: rpcError } = await supabase.rpc("set_knowledgebase_include_in_rag", {
                p_id: id,
                p_user_id: userId,
                p_include_in_rag: updates.includeInRag,
            });
            if (rpcError) {
                console.error("RPC set_knowledgebase_include_in_rag error:", rpcError);
                throw new Error(rpcError.message || "Failed to update Include in RAG. Run migration 006_set_include_in_rag_rpc.sql in Supabase.");
            }
            if (onlyIncludeInRag && rpcData && typeof rpcData === "object" && !(rpcData as { ok?: boolean }).ok) {
                const d = rpcData as { ok?: boolean; message?: string };
                if (d.ok === false) return null;
            }
            if (onlyIncludeInRag && rpcData && (rpcData as { id?: string }).id) {
                return transformToFrontend(rpcData as any);
            }
        }

        if (!isOwn && spaceContextId) {
            const pUpdates: Record<string, unknown> = {
                metadata: updateData.metadata,
            };
            if (updateData.title !== undefined) pUpdates.title = updateData.title;
            if (updateData.content !== undefined) pUpdates.content = updateData.content;
            if (updateData.spaceId !== undefined) pUpdates.spaceId = updateData.spaceId;
            if (updateData.type !== undefined) pUpdates.type = updateData.type;
            if (updateData.folderId !== undefined) pUpdates.folderId = updateData.folderId;
            if (updateData.googleDocsId !== undefined) pUpdates.googleDocsId = updateData.googleDocsId;
            if (updateData.videoUrl !== undefined) pUpdates.videoUrl = updateData.videoUrl;

            const { data: rpcUp, error: rpcUpErr } = await supabase.rpc("shared_update_knowledgebase_item", {
                p_id: id,
                p_space_id: spaceContextId,
                p_updates: pUpdates as any,
            });
            if (rpcUpErr) {
                throw new Error(rpcUpErr.message || "Failed to update document (shared)");
            }
            if (!rpcUp) return null;
            const raw = typeof rpcUp === "object" && rpcUp !== null && !Array.isArray(rpcUp)
                ? rpcUp
                : JSON.parse(String(rpcUp));
            const item = transformToFrontend(raw);
            if (updates.content !== undefined && String(updates.content ?? "").trim().length > 0
                && getThoughtProcessPreferences().autoEmbedDocuments) {
                ragService.scheduleEmbedDocument(id);
            }
            if (updates.content !== undefined) scheduleOperatingBrief(item.userId, item.id, item.title, item.content);
            return item;
        }

        const { data, error } = await supabase
            .from("knowledgebase")
            .update(updateData)
            .eq("id", id)
            .eq("userId", userId)
            .select()
            .single();

        if (error) {
            if (error.code === "PGRST116") {
                return null;
            }
            console.error("Error updating knowledgebase item:", error);
            throw new Error(`Failed to update knowledgebase item: ${error.message}`);
        }

        if (!data) return null;

        const item = transformToFrontend(data);
        if (updates.content !== undefined && String(updates.content ?? "").trim().length > 0
            && getThoughtProcessPreferences().autoEmbedDocuments) {
            ragService.scheduleEmbedDocument(id);
        }
        if (updates.content !== undefined) scheduleOperatingBrief(item.userId, item.id, item.title, item.content);
        return item;
    }

    /**
     * Delete a knowledgebase item
     * @param spaceContextId - Required when deleting another user's doc as space owner in a shared space.
     */
    async deleteKnowledgeBaseItem(id: string, spaceContextId?: string | null): Promise<boolean> {
        const userId = await this.getCurrentUserId();

        const existing = await this.getKnowledgeBaseItem(id);
        if (!existing) {
            return false;
        }
        const isOwn = existing.userId === userId;
        if (!isOwn) {
            if (!spaceContextId) {
                throw new Error("Cannot delete this document");
            }
            const { error: rpcErr } = await supabase.rpc("shared_delete_knowledgebase_item", {
                p_id: id,
                p_space_id: spaceContextId,
            });
            if (rpcErr) {
                throw new Error(rpcErr.message || "Failed to delete document");
            }
            return true;
        }

        const { error } = await supabase
            .from("knowledgebase")
            .delete()
            .eq("id", id)
            .eq("userId", userId);

        if (error) {
            console.error("Error deleting knowledgebase item:", error);
            throw new Error(`Failed to delete knowledgebase item: ${error.message}`);
        }

        return true;
    }

    /**
     * Parse metadata field, handling both string and object formats
     */
    private parseMetadata(metadata: any): Record<string, any> {
        if (!metadata) return {};
        if (typeof metadata === 'string') {
            try {
                return JSON.parse(metadata);
            } catch {
                return {};
            }
        }
        return metadata;
    }

    /**
     * Toggle document as source
     * Stores isSource in metadata for persistence
     */
    async toggleDocumentAsSource(id: string, isSource: boolean): Promise<KnowledgeBaseItem | null> {
        const userId = await this.getCurrentUserId();

        const existing = await this.getKnowledgeBaseItem(id);
        if (!existing) {
            throw new Error("Document not found");
        }

        const existingMetadata = this.parseMetadata(existing.metadata);
        const updatedMetadata = {
            ...existingMetadata,
            isSource: isSource,
        };

        const { data, error } = await supabase
            .from("knowledgebase")
            .update({
                metadata: updatedMetadata,
                updatedAt: new Date().toISOString(),
            })
            .eq("id", id)
            .eq("userId", userId)
            .select()
            .single();

        if (error) {
            console.error("Error toggling source status:", error);
            throw new Error(`Failed to update source status: ${error.message}`);
        }

        if (!data) return null;
        return transformToFrontend(data);
    }

    /**
     * Link an existing document to a space and optionally a topic (merge with existing links).
     * Uses existing metadata spaceIds/topicIds; no new DB tables.
     */
    async linkDocumentToSpaceTopic(
        documentId: string,
        spaceId: string,
        topicId?: string | null
    ): Promise<KnowledgeBaseItem | null> {
        const existing = await this.getKnowledgeBaseItem(documentId);
        if (!existing) return null;

        const meta = this.parseMetadata(existing.metadata);
        const currentSpaceIds: string[] = Array.isArray(meta.spaceIds)
            ? meta.spaceIds.map((id: unknown) => String(id))
            : meta.spaceId
                ? [String(meta.spaceId)]
                : existing.spaceId
                    ? [String(existing.spaceId)]
                    : [];
        const currentTopicIds: string[] = Array.isArray(meta.topicIds)
            ? meta.topicIds.map((id: unknown) => String(id))
            : meta.topicId
                ? [String(meta.topicId)]
                : existing.topicId
                    ? [String(existing.topicId)]
                    : [];

        const newSpaceIds = currentSpaceIds.includes(spaceId) ? currentSpaceIds : [...currentSpaceIds, spaceId];
        const newTopicIds =
            topicId && !currentTopicIds.includes(topicId) ? [...currentTopicIds, topicId] : currentTopicIds;

        return this.updateKnowledgeBaseItem(documentId, { spaceIds: newSpaceIds, topicIds: newTopicIds });
    }

    /**
     * Lock or unlock a knowledge base document
     */
    async lockDocument(id: string, lock: boolean): Promise<KnowledgeBaseItem | null> {
        const userId = await this.getCurrentUserId();

        const existing = await this.getKnowledgeBaseItem(id);
        if (!existing) {
            throw new Error("Document not found");
        }

        const existingMetadata = this.parseMetadata(existing.metadata);
        const lockedAtIso = lock ? new Date().toISOString() : null;
        const updatedMetadata = {
            ...existingMetadata,
            isLocked: lock,
            lockedAt: lockedAtIso,
        };

        const updateData: Record<string, unknown> = {
            metadata: updatedMetadata,
            updatedAt: new Date().toISOString(),
            is_locked: lock,
            locked_at: lockedAtIso,
        };

        const { data, error } = await supabase
            .from("knowledgebase")
            .update(updateData)
            .eq("id", id)
            .eq("userId", userId)
            .select()
            .single();

        if (error) {
            console.error("Error locking/unlocking document:", error);
            throw new Error(`Failed to update lock status: ${error.message}`);
        }

        if (!data) return null;
        return transformToFrontend(data);
    }

    /**
     * Duplicate a knowledge base document
     * Creates a copy with "(Copy)" appended to the title
     */
    async duplicateDocument(id: string): Promise<KnowledgeBaseItem> {
        const original = await this.getKnowledgeBaseItem(id);
        if (!original) {
            throw new Error("Document not found");
        }

        const newDoc = await this.createKnowledgeBaseItem({
            title: `${original.title} (Copy)`,
            content: original.content,
            spaceId: original.spaceId,
            topicId: original.topicId,
            tagIds: original.tagIds,
            type: original.type,
            folderId: original.folderId,
            metadata: {
                duplicatedFrom: original.id,
                duplicatedAt: new Date().toISOString(),
            },
        });

        return newDoc;
    }

    /**
     * Merge two documents
     * Appends content from sourceId to targetId, then deletes the source document
     */
    async mergeDocuments(sourceId: string, targetId: string): Promise<KnowledgeBaseItem> {
        const [source, target] = await Promise.all([
            this.getKnowledgeBaseItem(sourceId),
            this.getKnowledgeBaseItem(targetId),
        ]);

        if (!source) {
            throw new Error("Source document not found");
        }
        if (!target) {
            throw new Error("Target document not found");
        }

        const mergedContent = `${target.content || ""}\n\n---\n\n<p><em>Merged from: ${source.title}</em></p>\n\n${source.content || ""}`;

        const updated = await this.updateKnowledgeBaseItem(targetId, {
            content: mergedContent,
            metadata: {
                mergedFrom: [
                    ...(target.metadata?.mergedFrom || []),
                    { id: source.id, title: source.title, mergedAt: new Date().toISOString() }
                ],
            },
        });

        if (!updated) {
            throw new Error("Failed to update target document");
        }

        await this.deleteKnowledgeBaseItem(sourceId);

        return updated;
    }

    /**
     * Search documents by content (full-text search)
     */
    async searchDocuments(query: string): Promise<KnowledgeBaseItem[]> {
        const userId = await this.getCurrentUserId();
        
        if (!query.trim()) {
            return [];
        }

        const searchTerm = `%${query.toLowerCase()}%`;

        const { data, error } = await supabase
            .from("knowledgebase")
            .select("id, userId, title, spaceId, type, content, folderId, googleDocsId, videoUrl, metadata, createdAt, updatedAt, isLocked")
            .eq("userId", userId)
            .or(`title.ilike.${searchTerm},content.ilike.${searchTerm}`)
            .order("updatedAt", { ascending: false })
            .limit(50);

        if (error) {
            console.error("Error searching documents:", error);
            throw new Error(`Failed to search documents: ${error.message}`);
        }

        return (data || []).map(transformToFrontend);
    }
}

/**
 * Create and export a singleton instance
 */
export const knowledgeBaseService = new KnowledgeBaseService();
